import React, {useEffect, useRef} from 'react'
import {useHeaderContext} from '../contexts/header-context'
import {useLocation} from 'react-router-dom'

export const useStickyThreshold = () => {
    const location = useLocation()
    const {headerHeight, setShouldShowSticky} = useHeaderContext()
    const filtersRef = useRef(null)
    const overlayRef = useRef(null)
    const observerRef = useRef(null)
    const stickyRef = useRef(false)
    const debounceRef = useRef(null)

    useEffect(() => {
        if (!filtersRef.current || headerHeight === 0) return

        // If an observer already exists, don't recreate it. Recreating
        // the IntersectionObserver when headerHeight changes (from the
        // sticky bar being portaled into the header) triggers an immediate
        // intersection check on iOS that causes a toggle feedback loop
        // and visible screen flickering during momentum scrolling.
        if (observerRef.current) return

        observerRef.current = new IntersectionObserver(
            ([{isIntersecting}]) => {
                const newSticky = !isIntersecting
                if (stickyRef.current !== newSticky) {
                    // Debounce to prevent rapid toggling on iOS where momentum
                    // scrolling causes the intersection ratio to oscillate
                    // around the threshold.
                    clearTimeout(debounceRef.current)
                    debounceRef.current = setTimeout(() => {
                        stickyRef.current = newSticky
                        setShouldShowSticky(newSticky)
                    }, 60)
                }
            },
            {
                rootMargin: `-${headerHeight}px 0px 0px 0px`,
                threshold: 0.25
            }
        )

        observerRef.current.observe(filtersRef.current)
    }, [headerHeight, location.pathname, setShouldShowSticky])

    // Synchronous safety-net: during very fast UPWARD scrolls the
    // IntersectionObserver callback can lag a frame or two — and scroll events
    // themselves can be coalesced by the browser — leaving the sticky overlay
    // rendered on top of the reappearing site header. We schedule a single rAF
    // check after each upward scroll event (skip if one is already pending) so
    // the threshold is re-evaluated on the next paint regardless of scroll
    // speed, without running on downward scrolls where the sticky overlay
    // should remain.
    useEffect(() => {
        if (headerHeight === 0) return

        let rafId = null
        let lastY = typeof window !== 'undefined' ? window.scrollY : 0

        const check = () => {
            rafId = null
            if (!stickyRef.current || !filtersRef.current) return
            const top = filtersRef.current.getBoundingClientRect().top

            // Hide the overlay imperatively (bypassing React render lag) only
            // when the in-flow filters are within one viewport height of coming
            // back into view. When the user is in the middle of the page and
            // briefly scrolls up, top is still deeply negative so the overlay
            // remains visible.
            if (overlayRef.current) {
                const nearTop = top > -(window.innerHeight * 0.3)
                overlayRef.current.style.visibility = nearTop ? 'hidden' : ''
            }

            if (top >= headerHeight) {
                clearTimeout(debounceRef.current)
                stickyRef.current = false
                setShouldShowSticky(false)
            }
        }

        const onScroll = () => {
            const currentY = window.scrollY
            const scrollingUp = currentY < lastY
            lastY = currentY
            if (scrollingUp) {
                if (rafId != null) return
                rafId = requestAnimationFrame(check)
            } else {
                // User reversed direction (scrolling down again): restore
                // visibility in case the near-top hide had already fired.
                if (stickyRef.current && overlayRef.current) {
                    overlayRef.current.style.visibility = ''
                }
            }
        }

        window.addEventListener('scroll', onScroll, {passive: true})
        return () => {
            window.removeEventListener('scroll', onScroll)
            if (rafId != null) cancelAnimationFrame(rafId)
        }
    }, [headerHeight, setShouldShowSticky])

    // Clean up observer and reset sticky on route change or unmount
    useEffect(() => {
        return () => {
            clearTimeout(debounceRef.current)
            observerRef.current?.disconnect()
            observerRef.current = null
            stickyRef.current = false
            setShouldShowSticky(false)
        }
    }, [location.pathname, setShouldShowSticky])

    return {filtersRef, overlayRef}
}
